import { useState, useRef, useEffect } from "react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { SeoHead } from "@/components/public/SeoHead";
import { ModalScroll } from "@/components/public/ModalScroll";
import {
  X,
  MessageCircle,
  Sparkles,
  Tags,
  ShieldAlert,
  ShieldCheck,
  UploadCloud,
} from "lucide-react";
import { GdprRequestForm } from "@/components/public/GdprRequestForm";

const CATEGORIES = [
  {
    key: "general",
    title: "General inquiry",
    blurb: "Press, partnerships, advertising, anonymous tips, feedback on the site, or anything that doesn't fit the other tiles.",
    icon: MessageCircle,
  },
  {
    key: "brand_submission",
    title: "Suggest a brand",
    blurb: "Know a brand we should review? Tell us where to look and why they belong here.",
    icon: Sparkles,
  },
  {
    key: "material_correction",
    title: "Material correction",
    blurb: "Spotted an inaccurate material composition or classification on a product? Help us fix it.",
    icon: Tags,
  },
  {
    key: "false_advertising_report",
    title: "Report a label mismatch",
    blurb: "Found a product whose actual label doesn't match the brand's claim? Send us photos of the evidence.",
    icon: ShieldAlert,
  },
  {
    key: "data_request",
    title: "Data privacy request",
    blurb: "Request access to, correction of, deletion of, or restriction on your personal data we hold (GDPR rights).",
    icon: ShieldCheck,
  },
];

export default function ContactPage() {
  const [activeCategory, setActiveCategory] = useState(null);
  const [showThankYou, setShowThankYou] = useState(false);

  const active = CATEGORIES.find((c) => c.key === activeCategory) || null;

  return (
    <div>
      <SeoHead title="Contact — Sustainably Curated" description="Brand submissions, material corrections, label mismatch reports, or general inquiries." path="/contact" />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8">
        <div className="max-w-2xl">
          <h1 className="text-[1.75rem] uppercase tracking-[0.25em] text-leaf font-medium mb-3">Contact</h1>
          <p className="text-muted_text">Pick the topic that fits. Each opens the right form — we read every message personally.</p>
        </div>
      </section>

      <div className="border-t border-line" />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="contact-tiles">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setActiveCategory(c.key)}
            className="group text-left bg-white border border-line rounded-2xl p-6 hover:border-forest hover:shadow-lg transition-all hover:-translate-y-0.5"
            data-testid={`contact-tile-${c.key}`}
          >
            <div className="flex items-center gap-3 mb-2">
              <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-forest/8 text-forest group-hover:bg-forest group-hover:text-ivory transition-colors shrink-0">
                <c.icon className="w-4 h-4" />
              </div>
              <h3 className="font-serif text-xl leading-tight">{c.title}</h3>
            </div>
            <p className="text-sm text-muted_text leading-relaxed">{c.blurb}</p>
          </button>
        ))}
      </div>
      </section>

      {active && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={(e) => {
            // Close only when the actual backdrop is clicked (not children or Cloudinary widget)
            if (e.target === e.currentTarget) setActiveCategory(null);
          }}
          data-testid="contact-dialog-backdrop"
        >
          <div
            className="bg-white border border-line shadow-2xl max-w-lg w-full h-[90vh] flex flex-col overflow-hidden"
            data-testid="contact-dialog"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Fixed header */}
            <div className="px-6 sm:px-8 pt-6 pb-3 border-b border-line">
              <div className="flex items-start justify-between gap-4 mb-2">
                <h2 className="font-serif text-2xl flex items-center gap-2">
                  <active.icon className="w-5 h-5 text-forest" />
                  {active.title}
                </h2>
                <button
                  type="button"
                  onClick={() => setActiveCategory(null)}
                  className="shrink-0 text-muted_text hover:text-ink transition-colors p-1"
                  aria-label="Close"
                  data-testid="contact-dialog-close"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <p className="text-sm text-muted_text">{active.blurb}</p>
            </div>
            {active.key === "data_request" ? (
              <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-5">
                <GdprRequestForm onSuccess={() => { /* keep modal open to show success state */ }} />
              </div>
            ) : (
              <ContactForm
                category={active}
                onSuccess={() => {
                  setActiveCategory(null);
                  setShowThankYou(true);
                }}
              />
            )}
          </div>
        </div>
      )}

      {showThankYou && (
        <div
          className="fixed inset-0 z-40 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setShowThankYou(false); }}
          data-testid="contact-thankyou-backdrop"
        >
          <div
            className="bg-white border border-line shadow-2xl max-w-md w-full p-8 text-center relative"
            data-testid="contact-thankyou-dialog"
          >
            <button
              type="button"
              onClick={() => setShowThankYou(false)}
              className="absolute top-3 right-3 text-muted_text hover:text-ink transition-colors p-1"
              aria-label="Close"
              data-testid="contact-thankyou-close"
            >
              <X className="w-5 h-5" />
            </button>
            <h2 className="font-serif text-2xl mb-3">Thank you.</h2>
            <p className="text-sm text-muted_text mb-6">Your message has been received. We read every one personally.</p>
            <button
              type="button"
              onClick={() => setShowThankYou(false)}
              className="bg-forest text-ivory px-6 py-2.5 text-sm hover:bg-forest/90"
              data-testid="contact-thankyou-dismiss"
            >
              Send another message
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ContactForm({ category, onSuccess }) {
  const [form, setForm] = useState({
    name: "", email: "", subject: "", message: "",
    brand_url: "",
    item_url_on_site: "",
  });
  // Files held in browser memory only — uploaded server-side ONLY on submit.
  const [files, setFiles] = useState([]); // [{ file: File, previewUrl: string }]
  const [loading, setLoading] = useState(false);
  const [itemUrlError, setItemUrlError] = useState("");
  const fileInputRef = useRef(null);

  const MAX_FILES = 10;
  const MAX_FILE_BYTES = 8 * 1024 * 1024;
  const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp", "image/heic", "image/heif"];

  // Revoke object URLs on unmount to avoid leaks
  useEffect(() => () => { files.forEach((f) => URL.revokeObjectURL(f.previewUrl)); }, [files]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const siteOrigin = (typeof window !== "undefined" && window.location?.origin) || "";

  function validateItemUrl(value) {
    const v = (value || "").trim();
    if (!v) return "This field is required.";
    try {
      const u = new URL(v);
      if (siteOrigin && u.origin !== siteOrigin) {
        return "URL must point to a clothing item on this website.";
      }
      const parts = u.pathname.split("/").filter(Boolean);
      if (parts.length < 2 || parts[0] !== "clothing") {
        return "URL must point to a clothing item page (e.g. /clothing/<slug>).";
      }
    } catch {
      return "Enter a valid URL.";
    }
    return "";
  }

  const needsItemUrl = category.key === "material_correction" || category.key === "false_advertising_report";
  const needsBrandUrl = category.key === "brand_submission";
  const needsEvidence = category.key === "false_advertising_report";

  function onItemUrlChange(e) {
    const value = e.target.value;
    setForm((f) => ({ ...f, item_url_on_site: value }));
    setItemUrlError(value ? validateItemUrl(value) : "");
  }

  function onPickFiles(e) {
    const picked = Array.from(e.target.files || []);
    e.target.value = "";  // allow re-picking the same file later
    const accepted = [];
    for (const f of picked) {
      if (files.length + accepted.length >= MAX_FILES) {
        toast.error(`You can attach at most ${MAX_FILES} files.`);
        break;
      }
      if (!ALLOWED_TYPES.includes(f.type)) {
        toast.error(`"${f.name}": unsupported file type.`);
        continue;
      }
      if (f.size > MAX_FILE_BYTES) {
        toast.error(`"${f.name}" exceeds 8 MB.`);
        continue;
      }
      accepted.push({ file: f, previewUrl: URL.createObjectURL(f) });
    }
    if (accepted.length) {
      setFiles((prev) => [...prev, ...accepted]);
    }
  }

  function removeFile(i) {
    setFiles((prev) => {
      const removed = prev[i];
      if (removed) URL.revokeObjectURL(removed.previewUrl);
      return prev.filter((_, idx) => idx !== i);
    });
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (needsItemUrl) {
      const err = validateItemUrl(form.item_url_on_site);
      if (err) { setItemUrlError(err); return; }
    }
    if (needsEvidence && files.length === 0) {
      toast.error("Please attach at least one photo as evidence.");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("email", form.email);
      fd.append("category", category.key);
      fd.append("subject", form.subject || "");
      fd.append("message", form.message);
      if (form.brand_url) fd.append("brand_url", form.brand_url);
      if (form.item_url_on_site) fd.append("item_url_on_site", form.item_url_on_site);
      for (const { file } of files) {
        fd.append("files", file, file.name);
      }
      await api.post("/public/contact", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60_000,
      });
      toast.success("Message sent — we'll be in touch.");
      onSuccess();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
    <form onSubmit={onSubmit} className="flex-1 flex flex-col min-h-0 overflow-hidden" data-testid="contact-form" encType="multipart/form-data">
      <ModalScroll className="px-6 sm:px-8 py-5">
        <div className="space-y-4 pb-8">
        <Field label="Name *">
          <input required value={form.name} onChange={set("name")} className="input" data-testid="contact-name-input" />
        </Field>
        <Field label="Email *">
          <input required type="email" value={form.email} onChange={set("email")} className="input" data-testid="contact-email-input" />
        </Field>
        <Field label="Subject">
          <input value={form.subject} onChange={set("subject")} className="input" data-testid="contact-subject-input" />
        </Field>

        {needsBrandUrl && (
          <Field label="Brand's website URL *">
            <input
              required
              type="url"
              value={form.brand_url}
              onChange={set("brand_url")}
              className="input"
              placeholder="https://brandname.com"
              data-testid="contact-brand-url-input"
            />
          </Field>
        )}

        {needsItemUrl && (
          <Field label="Clothing item URL on this website *">
            <input
              required
              type="url"
              value={form.item_url_on_site}
              onChange={onItemUrlChange}
              onBlur={() => form.item_url_on_site && setItemUrlError(validateItemUrl(form.item_url_on_site))}
              className={`input ${itemUrlError ? "border-clay" : ""}`}
              placeholder={`${siteOrigin || ""}/clothing/<slug>`}
              data-testid="contact-item-url-input"
            />
            {itemUrlError && (
              <span className="block text-xs text-clay mt-1" data-testid="contact-item-url-error">
                {itemUrlError}
              </span>
            )}
          </Field>
        )}

        <Field label="Message *">
          <textarea required rows={5} value={form.message} onChange={set("message")} className="input" data-testid="contact-message-input" />
        </Field>

        {needsEvidence && (
          <Field label="Evidence (photos of the label / product) *">
            <div className="space-y-2">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                onChange={onPickFiles}
                className="hidden"
                data-testid="contact-file-input"
              />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center gap-1.5 bg-white border border-line text-ink rounded-md px-3 py-1.5 text-sm hover:bg-[#F1F3F5]"
                data-testid="contact-file-pick-button"
              >
                <UploadCloud className="w-4 h-4" />
                {files.length === 0 ? "Select photos" : "Add more photos"}
              </button>
              {files.length === 0 && (
                <span className="block text-xs text-clay" data-testid="contact-evidence-error">
                  At least one evidence photo is required.
                </span>
              )}
              {files.length > 0 && (
                <div className="grid grid-cols-4 gap-2" data-testid="contact-file-previews">
                  {files.map((f, i) => (
                    <div key={i} className="relative">
                      <img src={f.previewUrl} alt="" className="aspect-square object-cover border border-line" />
                      <button
                        type="button"
                        onClick={() => removeFile(i)}
                        className="absolute top-1 right-1 bg-white p-0.5 border border-line shadow-sm"
                        aria-label={`Remove ${f.file.name}`}
                        data-testid={`contact-file-remove-${i}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                      <span className="block text-[10px] text-muted_text truncate mt-0.5" title={f.file.name}>{f.file.name}</span>
                    </div>
                  ))}
                </div>
              )}
              <span className="block text-[11px] text-muted_text">Up to {MAX_FILES} photos, 8&nbsp;MB each. JPG, PNG, WEBP, or HEIC.</span>
            </div>
          </Field>
        )}

        <p className="text-[11px] text-muted_text leading-relaxed pt-2 border-t border-line" data-testid="contact-privacy-notice">
          Your submission and any attached photos are stored on our servers
          until you ask us to delete them. See our{" "}
          <a href="/privacy" className="underline hover:text-forest">privacy notice</a>{" "}
          for details and how to request deletion.
        </p>
        </div>
      </ModalScroll>

      {/* Fixed footer */}
      <div className="border-t border-line px-6 sm:px-8 py-3 bg-white">
        <button
          type="submit"
          disabled={loading || (needsItemUrl && !!itemUrlError) || (needsEvidence && files.length === 0)}
          className="w-full bg-forest text-ivory px-7 py-3 text-sm hover:bg-forest/90 disabled:opacity-60 disabled:cursor-not-allowed"
          data-testid="contact-submit-button"
        >
          {loading ? "Sending…" : "Send message"}
        </button>
      </div>
    </form>

      <style>{`.input { width: 100%; padding: 10px 14px; border-radius: 0; border: 1px solid #E8E2D9; background: #fff; font-family: 'Outfit', sans-serif; font-size: 14px; }
      .input:focus { outline: none; border-color: #1B4332; }`}</style>
    </>
  );
}

function Field({ label, children }) {
  // Render trailing "*" as a clearly visible red asterisk
  const required = typeof label === "string" && label.trim().endsWith("*");
  const cleanLabel = required ? label.replace(/\s*\*\s*$/, "") : label;
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-wider text-muted_text mb-1.5">
        {cleanLabel}
        {required && <span className="text-clay ml-1 normal-case tracking-normal" aria-hidden="true">*</span>}
      </span>
      {children}
    </label>
  );
}
